import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ORIENTATION_TYPE_OPTION_IDS } from 'commons/constants';
import {
  customItem,
  readyToShipItem,
  decideLaterItem,
} from 'components/consumer/PDP/PDPShopTabsBar/itemContent';
import useScreen from 'hooks/useScreen';
import usePdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import PDPSelectionAlert from './PDPSelectionAlert/PDPSelectionAlert';
import PDPSelectUpholsteryInner from '../PDPSelectUpholstery';
import PDPImageSelector from '../PDPImageSelector';
import PDPImageAndTextSelector from '../PDPImageAndTextSelector';
import PDPTextDropdownOptionGroup from '../PDPTextDropdownOptionGroup';
import PDPUpsellProduct from '../PDPUpSellProduct';
import PDPExtraOptionSelector from '../PDPExtraOptionSelector';
import PDPSelectUpholsteryDecideLater from '../PDPSelectUpholstery/PDPSelectUpholsteryDecideLater';
import PDPSelectUpholsteryPlaceholder from '../PDPSelectUpholstery/PDPSelectUpholsteryPlaceholder';
import PDPChooseYourOptionsPlaceholder from './PDPChooseYourOptionsPlaceholder';

const getTabContentId = selectedTab => {
  if (selectedTab === customItem.id) {
    return customItem.contentId;
  }

  if (selectedTab === readyToShipItem.id) {
    return readyToShipItem.contentId;
  }

  if (decideLaterItem.id) {
    return decideLaterItem.contentId;
  }

  return '';
};

const PDPChooseYourOptionsUIInner = ({
  pdpIsModularBYOProduct,
  isDecideLaterTab,
  isEditOrder = false,
  isFFG = true,
  isRTSTab = false,
  loading = false,
  mode = 'pdp',
  options = [],
  onMoreMaterialsClick,
  productId = null,
  selectionValidity = '',
  slug = null,
  upholsteryOptionId,
  optionSelector = '',
  setMarxentSelectedFabric,
}) => {
  const { isMobile } = useScreen();
  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();

  if (loading) {
    return (
      <div
        className="flex w-full flex-col relative z-[3]"
        id={getTabContentId(pdpSelectedCustomizerTab)}
      >
        <div
          className={classNames(
            'mt-8 mx-0 mb-0 relative w-full last:[border-bottom:0] last:pb-0',
            {
              'mt-0 mx-0 mb-4': mode === 'pdp',
            }
          )}
        >
          <PDPSelectUpholsteryPlaceholder />
        </div>
      </div>
    );
  }

  const upholsteryOptionValue =
    !loading && upholsteryOptionId && options?.length
      ? options.find(opt => opt.id === upholsteryOptionId)
      : null;

  const isUpholsterySelectorDropdownMode =
    (optionSelector || '').toLowerCase() === 'dropdown';

  return (
    <div
      className="flex w-full flex-col relative z-[3]"
      id={getTabContentId(pdpSelectedCustomizerTab)}
    >
      {!loading && options?.length ? (
        <>
          {isUpholsterySelectorDropdownMode && (
            <div
              className={classNames(
                'mt-8 mx-0 mb-0 relative w-full last:[border-bottom:0] last:pb-0',
                {
                  'mt-0 mx-0 mb-4': mode === 'quickview',
                  '!z-[999]': mode !== 'pdp',
                }
              )}
              key="upholstery-selector-dropdown"
            >
              <PDPTextDropdownOptionGroup
                optionId={upholsteryOptionValue?.id}
                isEditOrder={isEditOrder}
                slug={slug}
              />
            </div>
          )}

          {isDecideLaterTab && !isUpholsterySelectorDropdownMode ? (
            !isMobile && <PDPSelectUpholsteryDecideLater />
          ) : upholsteryOptionValue && !isUpholsterySelectorDropdownMode ? (
            <div
              className={classNames(
                'mt-8 mx-0 mb-0 relative w-full last:[border-bottom:0] last:pb-0',
                'PDPChooseYourOptionsUI__section--select-upholstery-wrapper',
                {
                  'mt-0 mx-0 mb-4': mode === 'quickview',
                }
              )}
              key={upholsteryOptionValue.id}
            >
              <PDPSelectUpholsteryInner
                isEditOrder={isEditOrder}
                mode={mode}
                optionId={upholsteryOptionValue.id}
                onMoreMaterialsClick={onMoreMaterialsClick}
                productId={productId}
                slug={slug}
                setMarxentSelectedFabric={setMarxentSelectedFabric}
              />
            </div>
          ) : (
            <div className="PDPChooseYourOptionsUI__section--select-upholstery-wrapper-filler" />
          )}

          <div className="PDPChooseYourOptionsUI__option-sections-wrapper">
            {options.map(option => {
              // Hide wood stain for FFG products in Custom tab
              if (
                (isFFG && option.id === 19) ||
                option.id === upholsteryOptionId ||
                (pdpIsModularBYOProduct &&
                  ORIENTATION_TYPE_OPTION_IDS.includes(option.id)) ||
                ((option.id !== 17 || option.id !== 18) && isEditOrder)
              ) {
                return null;
              }

              if ((optionSelector || '').toLowerCase() === 'dropdown') {
                return (
                  <div
                    className={classNames(
                      'mt-8 mx-0 mb-0 relative w-full last:[border-bottom:0] last:pb-0',
                      {
                        'mt-0 mx-0 mb-4': mode === 'quickview',
                        '!z-[999]': mode !== 'pdp',
                      }
                    )}
                    key={option.id}
                  >
                    <PDPTextDropdownOptionGroup optionId={option.id} />
                  </div>
                );
              }
              switch (option.optionDisplayType) {
                case 'ImageSelector':
                  return (
                    <div
                      className={classNames(
                        'mt-8 mx-0 mb-0 relative w-full last:[border-bottom:0] last:pb-0',
                        {
                          'mt-0 mx-0 mb-4': mode === 'pdp',
                        }
                      )}
                      key={option.id}
                    >
                      <PDPImageSelector
                        isEditOrder={isEditOrder}
                        optionId={option.id}
                      />
                    </div>
                  );

                case 'TextSelector':
                case 'ImageAndTextSelector':
                  return (
                    <div
                      className={classNames(
                        'mt-8 mx-0 mb-0 relative w-full last:[border-bottom:0] last:pb-0',
                        {
                          'mt-0 mx-0 mb-4': mode === 'pdp',
                        }
                      )}
                      key={option.id}
                    >
                      <PDPImageAndTextSelector
                        isEditOrder={isEditOrder}
                        imageOrTextVariant={
                          option.optionDisplayType === 'TextSelector'
                            ? 'textOnly'
                            : 'regular'
                        }
                        mode={mode}
                        optionId={option.id}
                        productId={productId}
                        variant="regular"
                      />
                    </div>
                  );
                case 'PDPFullWidthImageAndTextSelector':
                  return (
                    <div
                      className={classNames(
                        'mt-8 mx-0 mb-0 relative w-full last:[border-bottom:0] last:pb-0',
                        {
                          'mt-0 mx-0 mb-4': mode === 'pdp',
                        }
                      )}
                      key={option.id}
                    >
                      <PDPImageAndTextSelector
                        isEditOrder={isEditOrder}
                        optionId={option.id}
                        productId={productId}
                        variant="fullWidth"
                      />
                    </div>
                  );
                case 'TextDropdownSelector':
                  return (
                    <div
                      className={classNames(
                        'mt-8 mx-0 mb-0 relative w-full last:[border-bottom:0] last:pb-0',
                        {
                          'mt-0 mx-0 mb-4': mode === 'quickview',
                          '!z-[999]': mode !== 'pdp',
                        }
                      )}
                      key={option.id}
                    >
                      <PDPTextDropdownOptionGroup
                        key={`${option.id}--PDPTextDropdownOptionGroup`}
                        optionId={option.id}
                      />
                    </div>
                  );
                case 'UpsellSelector':
                  return <PDPUpsellProduct productId={productId} slug={slug} />;
                case 'ExtraOptionSelector':
                  return (
                    <PDPExtraOptionSelector
                      key={`${option.id}--PDPExtraOptionSelector`}
                    />
                  );
                default:
                  return (
                    <div
                      className={classNames(
                        'mt-8 mx-0 mb-0 relative w-full last:[border-bottom:0] last:pb-0',
                        {
                          'mt-0 mx-0 mb-4': mode === 'quickview',
                        }
                      )}
                      key={option.id}
                    >
                      <PDPTextDropdownOptionGroup optionId={option.id} />
                    </div>
                  );
              }
            })}
          </div>
        </>
      ) : (
        <>
          <div className="PDPChooseYourOptionsUI__PDPSelectUpholsteryDecideLater-filler" />
          <div className="PDPChooseYourOptionsUI__PDPSelectUpholsteryDecideLater-filler" />
        </>
      )}

      {!loading && isRTSTab && selectionValidity ? (
        <PDPSelectionAlert selectionValidity={selectionValidity} />
      ) : (
        <div className="PDPChooseYourOptionsUI__PDPSelectionAlert-filler" />
      )}
    </div>
  );
};

PDPChooseYourOptionsUIInner.propTypes = {
  isDecideLaterTab: PropTypes.bool,
  isEditOrder: PropTypes.bool,
  isFFG: PropTypes.bool,
  isRTSTab: PropTypes.bool,
  loading: PropTypes.bool,
  mode: PropTypes.oneOf(['pdp', 'quickview']),
  onMoreMaterialsClick: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.object),
  optionSelector: PropTypes.string,
  pdpIsModularBYOProduct: PropTypes.bool,
  productId: PropTypes.number,
  selectionValidity: PropTypes.string,
  setMarxentSelectedFabric: PropTypes.func,
  slug: PropTypes.string,
  upholsteryOptionId: PropTypes.number,
};

const PDPChooseYourOptionsUI = props => (
  <SuspenseWithLoader fallback={<PDPChooseYourOptionsPlaceholder />}>
    <PDPChooseYourOptionsUIInner {...props} />
  </SuspenseWithLoader>
);

export default PDPChooseYourOptionsUI;
